/**
 * ColorSwatch - Single color button for color selection.
 *
 * A clickable color circle used in color palettes. Shows a ring
 * when selected and scales up on hover.
 */

import { cn } from '@lean-agility/design-system/react';

export interface ColorSwatchProps {
  /** The color value (hex, rgb, etc.) */
  color: string;
  /** Whether this swatch is currently selected */
  selected?: boolean;
  /** Callback when the swatch is clicked */
  onClick: (color: string) => void;
  /** Size variant */
  size?: 'sm' | 'md';
  /** Additional CSS classes */
  className?: string;
}

const sizeStyles = {
  sm: { width: '24px', height: '24px' },
  md: { width: '28px', height: '28px' },
};

/**
 * ColorSwatch - Clickable color button for palettes.
 *
 * @example
 * ```tsx
 * <ColorSwatch
 *   color="#3b82f6"
 *   selected={selectedColor === '#3b82f6'}
 *   onClick={setSelectedColor}
 * />
 * ```
 */
export function ColorSwatch({ color, selected = false, onClick, size = 'md', className }: ColorSwatchProps) {
  return (
    <button
      type="button"
      onClick={() => onClick(color)}
      className={cn(
        `
          rounded-sm transition-all
          hover:scale-110
        `,
        selected &&
          `
            shadow-lg ring-2 ring-white ring-offset-1
            dark:ring-ring-offset
          `,
        className,
      )}
      style={{
        backgroundColor: color,
        ...sizeStyles[size],
      }}
      title={color}
      aria-label={`Select color ${color}`}
      aria-pressed={selected}
    />
  );
}
